import type { ElementType, ReactNode } from 'react';

import type { CursorPagination } from '@shieldpay/steamhammer-ui/api';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import SvgInfoIcon from '../icons/InfoIcon';

import { Box } from '../box/box';
import { Link } from '../link/link';
import { Text } from '../text/text';

import * as styles from './pagination.css';

export interface PaginationProps {
  /**
   * Total number of results available now.
   */
  pageSize: number;
  totalCount: CursorPagination['totalCount'];
  loadMoreTo: string | null;
  helpText?: ReactNode;
  linkLabel: ReactNode;
  paginationResults: (paginationTextInfo: {
    totalCount: number | 'many';
    pageSize: number;
  }) => ReactNode;
  'data-testid'?: string;
  loading?: boolean;
  NavLink: ElementType;
}

const TEST_IDS = {
  PAGINATION: 'pagination-component',
};

export const Pagination = ({
  loadMoreTo,
  pageSize,
  totalCount,
  paginationResults,
  linkLabel,
  helpText,
  loading = false,
  'data-testid': testId = TEST_IDS.PAGINATION,
  NavLink,
}: PaginationProps) => {
  return (
    <Box
      data-testid={testId}
      alignContent={{ x: 'center' }}
      padding={{ y: 'basePos3' }}
      spacing="baseNeg2"
    >
      <Box stack="row" spacing="baseNeg4" alignContent={{ y: 'center' }}>
        <Text paragraph variant="input">
          {paginationResults({
            totalCount,
            pageSize,
          })}
        </Text>
        {helpText ? (
          <Box variant="initial" className={classFilter([styles.iconWrapper])}>
            <SvgInfoIcon size="s" />
            <Text
              className={classFilter([
                styles.tooltip,
                styles.tooltipFirefoxTextBugWorkaround,
              ])}
              color="neutral000"
              padding="baseNeg6"
              role="tooltip"
              truncate
              variant="captionSmall"
            >
              {helpText}
            </Text>
          </Box>
        ) : null}
      </Box>

      <Box stack="row" spacing="baseNeg4">
        {loadMoreTo ? (
          <Link
            variant="outline"
            to={loadMoreTo}
            preventScrollReset
            loading={loading}
            NavLink={NavLink}
          >
            {linkLabel}
          </Link>
        ) : null}
      </Box>
    </Box>
  );
};
